/**
 * Conflict map for a company roll-up (roadmap P-2): every item that more
 * than one organization lists, where each placed it, and whose rank the merge
 * will keep. Display only; the reading is `findRollupConflicts`.
 */

import type { RollupConflict } from '../../../lib/portfolioRollupConflicts';
import { DataTable } from '../../ds';

export function RollupConflictMap({
  conflicts,
  loading,
  error,
}: {
  conflicts: RollupConflict[];
  loading: boolean;
  error: string | null;
}) {
  return (
    <div className="border-t border-[var(--rule)] pt-4" data-testid="rollup-conflicts">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        Conflict map
      </h3>
      {error ? (
        <p className="mt-2 text-sm text-[var(--coral)]">{error}</p>
      ) : loading ? (
        <p className="mt-2 text-sm text-[var(--text-ter)]">Reading the lists…</p>
      ) : conflicts.length === 0 ? (
        <p className="mt-2 text-sm text-[var(--text-sec)]">
          No item is on more than one list, so the merge keeps every organization’s order.
        </p>
      ) : (
        <>
          <p className="mt-2 text-sm text-[var(--text-sec)]">
            {conflicts.length} item{conflicts.length === 1 ? ' is' : 's are'} on more than one list. The merge keeps
            one rank and one owner for each — the last list that holds it — and drops the others.
          </p>
          <div className="mt-3">
            <DataTable
              density="compact"
              rule="horizontal"
              ariaLabel="Items on more than one list"
              rows={conflicts}
              getRowKey={(c) => c.title}
              columns={[
                {
                  key: 'title',
                  header: 'Item',
                  render: (c) => (
                    <>
                      {c.title}
                      {c.differentOwners && (
                        <span className="ml-2 text-xs text-[var(--mustard)]">two owners</span>
                      )}
                    </>
                  ),
                },
                {
                  key: 'claims',
                  header: 'Where each list puts it',
                  render: (c) =>
                    c.claims.map((cl) => (
                      <div key={cl.listLabel} className="text-[var(--text-sec)]">
                        #{cl.position} on {cl.listLabel}
                        {cl.owningOrg ? ` (${cl.owningOrg})` : ''}
                      </div>
                    )),
                },
                { key: 'kept', header: 'The merge keeps', render: (c) => keptLabel(c) },
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
}

function keptLabel(c: RollupConflict): string {
  return `${c.kept.listLabel}${c.kept.owningOrg ? ` (${c.kept.owningOrg})` : ''}`;
}
